import ApiCollection            from '@widesk-core/models/ApiCollection';
import ContactContactGroupModel from '@/models/services/network/ContactContactGroupModel';
import ContactGroupModel        from '@/models/services/network/ContactGroupModel';
import ContactModel             from '@/models/services/network/ContactModel';
import EntityModel              from '@/models/services/fondations/EntityModel';
import FileModel                from '@/models/services/file/FileModel';
import React                    from 'react';
import useFetch                 from '@widesk-core/hooks/useFetch';

export interface EntityRolesData {
	gestionnaireContact: ContactModel | undefined;
	managerContacts:     ContactModel[];
	managerIds:          Set<string>;
	gestionnaireId:      string | undefined;
	assignableContacts:  ContactModel[];
	contactFiles:        { contactId: string; file: FileModel }[];
}

const EMPTY_ROLES: EntityRolesData = {
	gestionnaireContact: undefined,
	managerContacts:     [],
	managerIds:          new Set(),
	gestionnaireId:      undefined,
	assignableContacts:  [],
	contactFiles:        [],
};

/**
 * Fetches all contacts for a given entity (without photos) and determines roles:
 * gestionnaire de compte, managers, and assignable contacts.
 * Also batch-fetches photos for the small gestionnaire+managers subset.
 */
const useEntityRoles = (entity: EntityModel | undefined) => {
	const { data: roles, isLoading, fetchAsync } = useFetch(async (): Promise<EntityRolesData> => {
		if (!entity?.urn) return EMPTY_ROLES;

		const contactCollection = new ApiCollection(ContactModel);
		await contactCollection
			.setSort('firstname', 'asc')
			.setFilter('ownerUser.userUrn', entity.urn)
			.list();

		if (contactCollection.length === 0) return EMPTY_ROLES;

		const ccgCollection = new ApiCollection(ContactContactGroupModel);
		const cgCollection  = new ApiCollection(ContactGroupModel);

		await Promise.all([
			ccgCollection.setFilter('contact', contactCollection.ids).list(),
			cgCollection.list(),
		]);

		const accountManagerGroup = cgCollection.find(cg => cg.reference === 'account_manager');
		const managerGroup        = cgCollection.find(cg => cg.reference === 'manager');
		const allCCGs             = ccgCollection.map(ccg => ccg);

		const gestionnaireContactId = allCCGs
			.find(ccg => String(ccg.getId('contactGroup')) === String(accountManagerGroup?.id))
			?.getId('contact');

		const managerIds = new Set(
			allCCGs
				.filter(ccg => String(ccg.getId('contactGroup')) === String(managerGroup?.id))
				.map(ccg => String(ccg.getId('contact')))
		);

		const allContacts = contactCollection.map(c => c);

		const gestionnaireContact = gestionnaireContactId != null
			? allContacts.find(c => String(c.id) === String(gestionnaireContactId))
			: undefined;

		const managerContacts    = allContacts.filter(c => managerIds.has(String(c.id)));
		const assignableContacts = allContacts.filter(c =>
			String(c.id) !== String(gestionnaireContactId) && !managerIds.has(String(c.id))
		);

		// Fetch photos only for gestionnaire + managers (small subset, typically 2-5 contacts)
		const relevantContacts = [gestionnaireContact, ...managerContacts].filter(Boolean) as ContactModel[];
		const contactFiles: { contactId: string; file: FileModel }[] = [];

		await Promise.all(
			relevantContacts.map(async (contact) => {
				const files = await new ApiCollection(FileModel)
					.setFilters({
						'typeSource.type.reference': 'contact_photo',
						'typeSource.source.entityUrn': [contact.get('@urn')],
					})
					.list();
				const file = files.models[0] as unknown as FileModel;
				if (file) contactFiles.push({ contactId: String(contact.id), file });
			}),
		);

		return {
			gestionnaireContact,
			managerContacts,
			managerIds,
			gestionnaireId: gestionnaireContactId != null ? String(gestionnaireContactId) : undefined,
			assignableContacts,
			contactFiles,
		};
	});

	// Trigger fetch when the entity becomes available (or changes)
	React.useEffect(() => {
		if (entity?.urn) fetchAsync();
	}, [entity?.urn]);

	return { roles: roles ?? null, isLoading, refetch: fetchAsync };
};

export default useEntityRoles;
